/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { AuthSharedModule, LOGGED_IN_CONTEXT_SERVICE } from '@irian-ro/auth-shared-module';
import {
  EnvironmentModule,
  SharedComponentsModule as IriroSharedComponentsModule,
  UiBlockerHttpInterceptor,
} from '@irian-ro/shared-components-module';
import { ErrorModule } from '@well/error';
import {
  HttpTimeoutInterceptor,
  LoggedInContextService,
  SharedProvidersModule,
} from '@well/shared-providers';
import { SharedComponentsModule } from '@well/shared-components';
import { environment } from '../environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { HistoryMenuModule } from '@well/history-menu';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { oidcAuthorizationInterceptor, oidcInvalidTokenInterceptor } from '@irian-ro/auth-oidc';
import { NavigationModule } from '@irian-ro/navigation-module';
import { StompModule } from '@irian-ro/stomp-module';
import { MenubarModule } from 'primeng/menubar';
import { NotificationUiModule } from '@irian-ro/notification-ui-module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { FileStorageModule } from '@well/file-storage';
import { FirestoreModule } from '@angular/fire/firestore';
import { ExceptionModule } from '@irian-ro/exception-service-module';
import { ToastModule } from 'primeng/toast';
import { AngularSplitModule } from 'angular-split';
import { provideNgxWebstorage } from 'ngx-webstorage';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthModule } from '@irian-ro/auth-web-client';
import { RoutePopupComponent } from './components/route-popup/route-popup.component';
import { RightSideComponent } from './components/right-side/right-side.component';
import { RightSideAnimationComponent } from './components/right-side/right-side-animation/right-side-animation.component';
import { LoggedInTermsAndConditionsPageComponent } from './components/logged-in-terms-and-conditions-page/logged-in-terms-and-conditions-page.component';
import { StepsModule } from 'primeng/steps';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { RoutePopupFullComponent } from './components/route-popup-full/route-popup-full.component';
import { AngularFireModule } from '@angular/fire/compat';
import { ShellRoutingModule } from './shell-routing.module';
import { ServiceWorkerManagementModule } from '@irian-ro/service-worker-management';
import { InfraWellSystemSettingsModule } from '@well/infra/well-system-settings';
import { RoleWithCompanyTypeService, UserWithCompanyTypeService } from '@well/roles-and-users-management';
import { WellWorkflowsModule } from '@well/infra/workflows/definition';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { Drawer } from 'primeng/drawer';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { IriroFrontendLibsUtilMapsModule } from '@irian-ro/util-maps';
import { LegalEntityManagementModule } from '@well/common-libs/legal-entity-management/shared';
import { FooterComponent } from '@well/infra/footer';
import { langs } from '@well/translation';
import { ShipmentCommonStoreModule } from '@well/delivery-libs/shipment-common/store';
import { SelectItemsStateModule } from '@well/select-items-shell-api';
import { CommonLibsCountryManagementStateModule } from '@well/common-libs/country-management/state';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';
import { GuestTermsAndConditionsPageComponent } from './components/guest-terms-and-conditions-page/guest-terms-and-conditions-page.component';
import { SkeletonModule } from 'primeng/skeleton';
import { filtersProviders } from '../../../../libs/infra/column-filters-providers/src/lib/filters-providers';
import { providePrimeNG } from 'primeng/config';
import { BS_ZINDEX_SERVICE } from '@well/iriro-frontend-libs/bs-component-store';
import { PrimeNgZIndexService } from './primeng-zindex.service';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { provideOAuthClient, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { ExtendedOAuthService } from '@irian-ro/auth-oidc';
// @ts-ignore - PrimeNG 18 theme import
import Aura from '@primeuix/themes/aura';
// @ts-ignore - PrimeNG 18 theme import
import { definePreset } from '@primeuix/themes';

export function oauthStorageFactory(): OAuthStorage {
  return localStorage;
}

// Custom theme preset with primary color from theme.scss (#3f51b5)
const WellTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
});

export class WebpackTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return of((langs as Record<string, any>)[lang]);
  }
}

export class DefaultValueMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    const { interpolateParams, key } = params;
    if (interpolateParams) {
      const defaultValue = (interpolateParams as Record<string, any>)['defaultValue'];
      return defaultValue ? defaultValue : key;
    }
    return key;
  }
}

@NgModule({
  declarations: [AppComponent, LoggedInTermsAndConditionsPageComponent, GuestTermsAndConditionsPageComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TooltipModule, MenuModule, 
    BrowserModule,
    RouterModule,
    AngularSplitModule,
    IriroFrontendLibsUtilMapsModule.forRoot({ googleMapsKey: environment.googleApiKey }),
    BrowserAnimationsModule,
    ReactiveFormsModule,
    EnvironmentModule.forRoot(environment),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: DefaultValueMissingTranslationHandler,
      },
      useDefaultLang: false,
    }),
    AuthSharedModule.forRoot(
      { storage: 'local-storage' },
      {
        provide: LOGGED_IN_CONTEXT_SERVICE,
        useClass: LoggedInContextService,
      }
    ),
    ShellRoutingModule,
    HistoryMenuModule.forRoot({ maxBufferSize: 5 }),
    StompModule.forRoot({ serverUrl: environment.wsEndpointUrl }),
    NavigationModule.forRoot([{ key: 'test', link: 'resource-availability/timeline' }], {}, {}),
    NotificationUiModule.forRoot(),
    FormsModule,
    SharedComponentsModule,
    IriroSharedComponentsModule.forRoot(),
    ErrorModule,
    MenubarModule,
    SelectModule,
    ButtonModule,
    ToastModule,
    AuthModule.forRoot({
      roleService: RoleWithCompanyTypeService,
      userSaveService: UserWithCompanyTypeService,
    }),
    SharedProvidersModule,
    FileStorageModule.forRoot({
      apiUrl: '/crm/api/attachment/',
    }),
    NgProgressModule,
    NgProgressHttpModule,
    AngularFireModule.initializeApp(environment.firebase),
    FirestoreModule,
    ExceptionModule.forRoot({}),
    StepsModule,
    LegalEntityManagementModule,
    CheckboxModule,
    MessagesModule,
    ServiceWorkerManagementModule.forRoot(environment.serviceWorker),
    InfraWellSystemSettingsModule,
    WellWorkflowsModule,
    ShipmentCommonStoreModule,
    Drawer,
    FooterComponent,
    SelectItemsStateModule,
    CommonLibsCountryManagementStateModule,
    LetDirective,
    SkeletonModule,
    RoutePopupComponent,
    RoutePopupFullComponent,
    SidebarComponent,
    RightSideComponent,
    RightSideAnimationComponent
  ],
  providers: [
    // Alias LoggedInContextService to use the same instance as LOGGED_IN_CONTEXT_SERVICE token
    // This ensures all direct injections of LoggedInContextService get the token-based instance
    {
      provide: LoggedInContextService,
      useExisting: LOGGED_IN_CONTEXT_SERVICE,
    },
    providePrimeNG({
      theme: {
        preset: WellTheme,
        options: {
          darkModeSelector: false,  // Disable dark mode support
        },
      },
    }),
    {
      provide: BS_ZINDEX_SERVICE,
      useClass: PrimeNgZIndexService,
    },
    provideNgxWebstorage({ prefix: 'well', separator: '-' }),
    filtersProviders,
    MessageService,
    ConfirmationService,
    provideFirebaseApp(() => initializeApp(environment.deliwellFirebase)),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UiBlockerHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTimeoutInterceptor,
      multi: true,
    },
    provideHttpClient(
      withInterceptors([oidcAuthorizationInterceptor, oidcInvalidTokenInterceptor]),
      withInterceptorsFromDi(),
      withJsonpSupport()
    ),
    provideOAuthClient(),
    { provide: OAuthService, useClass: ExtendedOAuthService },
    { provide: OAuthStorage, useFactory: oauthStorageFactory },
  ],
})
export class AppModule {
  router = inject(Router);

  constructor(private translateService: TranslateService) {
    const langsRecord = langs as Record<string, any>;
    Object.keys(langsRecord).map((lang) => {
      translateService.setTranslation(lang, langsRecord[lang], true);
    });
    translateService.setDefaultLang('ro');
    translateService.use('ro');

    this.router.events.pipe(filter((events) => events instanceof NavigationStart)).subscribe((events) => {
      if (events instanceof NavigationStart && events.url && events.url.startsWith('/#/')) {
        this.router.navigateByUrl(events.url.slice(3));
      }
    });
  }
}
